import HorizontalSeparator from "component/design-system/horizontal-separator/HorizontalSeparator";
import PAImage from "component/design-system/images/PAImage";
import PAText, { FONT_SIZE } from "component/design-system/typography/PAText/PAText";
import useScreenSize from "hoc/useScreenSize";
import Image from "next/legacy/image";
import { useRouter } from "next/router";
import HideIf from "util/HideIf";
import { getRelativeImagePath } from "util/helper/getRelativeImagePath";
import { colorMap } from "util/product/apparel";
import FilterableAttribute from "./FilterableAttribute";
import styles from "./apparel-component.module.scss";

interface IProps {
  alternateProducts: {
    productUrl: string;
    size: string;
    color: string;
    merchandiseColor?: string;
    merchandiseSize?: string;
    airFreshenerColor?: string;
    airFreshenerScent?: string;
    outOfStock: boolean;
  }[];
  handleSizeChartClick: () => void;
  packOf?: number;
}

const ColorandSize = ({
  alternateProducts, handleSectionClick, filterableAttributes, packOf, outOfStock, links,
}: IProps) => {
  const router = useRouter();
  const currentProductUrl = router.query.pathname;
  const { dimensions } = links;

  const {
    airFreshenerScent, merchandiseColor, merchandiseSize, airFreshenerColor,
  } = filterableAttributes || {};
  const products = [{
    productUrl: currentProductUrl,
    merchandiseColor,
    merchandiseSize,
    airFreshenerColor,
    airFreshenerScent,
    packOf,
    outOfStock,
  }, ...alternateProducts];
  const selectedColor = merchandiseColor || airFreshenerColor;
  const selectedSize = merchandiseSize;
  const getUniqueValues = (key: string, checkOutOfStock = false) => {
    const allProductValues = products.filter((product) => {
      if (!product[key]) {
        return false;
      }
      if (!checkOutOfStock) {
        return true;
      }
      return key === "packOf" ? product.airFreshenerScent === airFreshenerScent && !product.outOfStock : !product.outOfStock;
    })
      .map((product) => product[key]);
    return Array.from(new Set(allProductValues));
  };

  const merchandiseColors = getUniqueValues(filterableAttributes?.merchandiseColor ? "merchandiseColor" : "airFreshenerColor", true).sort();
  const sizes = getUniqueValues("merchandiseSize", true);
  const scents = getUniqueValues("airFreshenerScent", true);
  const packSizes = packOf === 1 ? [] : getUniqueValues("packOf", true);
  const showMerchandiseColor = merchandiseColors.length > 0;
  const showSize = sizes.length > 0;
  const showScents = scents.length > 0;
  const showPackSizes = packSizes.length > 0;

  const sizeOrder = ["XS", "S", "M", "L", "XL", "2XL", "3XL", "4XL"];
  const defaultSizes = getUniqueValues("merchandiseSize")
    .sort((a, b) => sizeOrder.indexOf(a) - sizeOrder.indexOf(b))
    .filter((size) => {
      if ((size === "XS" || size === "4XL") && ![...sizes, selectedSize].includes(size)) {
        return false;
      }
      return true;
    });
  const defaultScents = getUniqueValues("airFreshenerScent");
  const defaultPackSizes = getUniqueValues("packOf").sort((a, b) => a - b);

  const [isMobile] = useScreenSize();

  const handleSelect = (type: string, value: string, isProductAvailable = true) => {
    if (!isProductAvailable) {
      return;
    }

    let productToPushUrl = null;
    const clickedProduct = products.find((product) => product[type] === value);
    productToPushUrl = clickedProduct?.productUrl;

    if (type === "merchandiseColor") {
      const productWithColorandSize = products.find((product) => product.merchandiseColor === value && product.merchandiseSize === selectedSize);
      const productWithColor = products.find((product) => product.merchandiseColor === value);

      productToPushUrl = productWithColorandSize ? productWithColorandSize.productUrl : productWithColor?.productUrl;
    }

    if (type === "merchandiseSize" && sizes.includes(value)) {
      const productWithSizeandColor = products.find((product) => product.merchandiseColor === selectedColor && product.merchandiseSize === value);
      const productWithSize = products.find((product) => product.merchandiseSize === value);

      productToPushUrl = productWithSizeandColor ? productWithSizeandColor.productUrl : productWithSize?.productUrl;
    }

    if (type === "airFreshenerScent" && packOf > 1) {
      const productScentAndPack = products.find((product) => product.airFreshenerScent === value && product.packOf === packOf);
      const productScent = products.find((product) => product.airFreshenerScent === value);

      productToPushUrl = productScentAndPack ? productScentAndPack.productUrl : productScent?.productUrl;
    }

    if (productToPushUrl) {
      router.replace(productToPushUrl);
    }
  };

  return (
    <div className="m-b-20">
      <HideIf show={showMerchandiseColor}>
        <div
          className="m-t-10 m-b-20"
        >
          <PAText
            fontSize={isMobile ? FONT_SIZE[3] : FONT_SIZE[2]}
            bold
          >
            {merchandiseColors.length > 1 ? "Select Color:" : "Color:"}
          </PAText>

          <PAText
            fontSize={isMobile ? FONT_SIZE[1] : FONT_SIZE[2]}
            color="grey"
            className="m-l-5"
          >
            {selectedColor}
          </PAText>
        </div>

        <div className={styles.color_container}>
          {merchandiseColors.map((color) => (
            <div
              key={color}
              className={`${styles.color_circle} ${selectedColor === color && styles.selected_color }`}
            >
              <Image
                className={styles.color_circle}
                onClick={() => handleSelect("merchandiseColor", color)}
                src={getRelativeImagePath(colorMap[color])}
                width={50}
                height={50}
              />
            </div>
          ))}
        </div>
        <HorizontalSeparator />
      </HideIf>

      <HideIf show={showScents}>
        <FilterableAttribute
          defaultValues={defaultScents}
          availableValues={scents}
          selectedValue={airFreshenerScent}
          title="Select Scent"
          attribute="airFreshenerScent"
          handleFilterClick={handleSelect}
          scrollToSection={dimensions?.length && "Dimensions"}
          scrollSectionImg="/cms/pubicImages/images/Dimension.webp"
          handleSectionClick={() => handleSectionClick("dimensions")}
        />
        <HorizontalSeparator />
      </HideIf>
      <HideIf show={showPackSizes}>
        <FilterableAttribute
          defaultValues={defaultPackSizes}
          availableValues={packSizes}
          selectedValue={packOf}
          title="Select Pack Size"
          attribute="packOf"
          handleFilterClick={handleSelect}
          scrollToSection={dimensions?.length && "Dimensions"}
          scrollSectionImg="/cms/pubicImages/images/Dimension.webp"
          handleSectionClick={() => handleSectionClick("dimensions")}
        />
      </HideIf>

      <HideIf show={showSize}>
        <div className={`${styles.size_select_row} m-t-10 m-b-20`}>
          <PAText
            fontSize={isMobile ? FONT_SIZE[3] : FONT_SIZE[2]}
            bold
          >
            Select Size
          </PAText>

          <div className={styles.size_chart_select} onClick={() => handleSectionClick("size_chart")}>
            <PAImage
              src={getRelativeImagePath("/images/size_chart_icon.png")}
              alt="Size Chart"
              width={50}
              height={50}
            />
            <PAText
              fontSize={isMobile ? FONT_SIZE[1] : FONT_SIZE[2]}
              {...!isMobile && { bold: true }}
            >
              Size Chart
            </PAText>
          </div>
        </div>

        <div className={styles.size_container}>
          {defaultSizes.map((size) => (
            <div
              key={size}
              className={`${styles.size_box} ${selectedSize === size && styles.selected_size} ${![...sizes, selectedSize].includes(size) && styles.size_not_available}`}
              onClick={() => handleSelect("merchandiseSize", size)}
            >
              {size}
            </div>
          ))}
        </div>
      </HideIf>
    </div>
  );
};

export default ColorandSize;
